import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import {
  SedeHeader,
  SedeHeaderText,
  GroupName,
  PartnerPill,
  SedeSubtitle,
  SedeSearch,
  SearchShortcut,
  SedeSectionLabel,
  SedeRows,
  SedeItem,
  SedeRowButton,
  SedeMonogram,
  SedeContent,
  SedeTitleLine,
  SedeLabel,
  ActiveMark,
  SedePhoneLine,
  SedePhones,
  SedePhone,
  SedeMorePhones,
  WaitingSlot,
  WaitingPill,
  NewTabButton,
  SedeEmptyLine,
  SedeMutedLine,
} from './SedeList.style';
import SedeOverviewCard from './SedeOverviewCard.component';
import { NewTabIcon, SearchIcon, WhatsappIcon } from './sedeIcons';
import { getWhatsappPhones, summarizeWhatsappPhones } from './whatsappPhones';

// The shortcut that opens the profile menu on this search, as each platform
// writes it.
const isApplePlatform = () => /Mac|iPhone|iPad|iPod/.test(window.navigator?.platform || '');

// The color of a sede comes from the backend: only a plain hex color reaches
// the page, anything else falls back to the color of the stylesheet.
const HEX_COLOR_PATTERN = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const monogramStyle = (color) => (HEX_COLOR_PATTERN.test(color) ? { backgroundColor: color } : undefined);

const onlyDigits = (value) => String(value).replace(/\D/g, '');

const sedeCount = (count, suffix) => `${count} ${count === 1 ? 'sede' : 'sedes'} ${suffix}`;

// A sede can be picked when it has a WhatsApp number to work.
const toSelectable = (sede) => {
  const phones = getWhatsappPhones(sede.channels);
  return {
    ...sede,
    // One line in the row; the filter still looks in every phone.
    phones: summarizeWhatsappPhones(sede.channels),
    phoneDigits: phones.map(onlyDigits),
  };
};

/** The sedes of a directory that can be picked, ready for a row. */
export const getSelectableSedes = (sedes) => sedes.map(toSelectable).filter((sede) => sede.phones !== null);

const matches = (sede, query) => {
  const text = query.trim().toLowerCase();
  if (text === '') return true;
  if (sede.label.toLowerCase().includes(text)) return true;
  // Only something that reads as a phone is looked up in the numbers.
  const digits = onlyDigits(text);
  const isPhoneLike = digits !== '' && /^[\d\s()+-]+$/.test(text);
  return isPhoneLike && sede.phoneDigits.some((phone) => phone.includes(digits));
};

/**
 * The sedes of a partner: group header, search and one row per sede with
 * WhatsApp. It only renders what it is given and tells the parent what was
 * picked; where the sedes come from and what picking one does is up to the
 * parent (the profile menu, the chat). Its blocks are siblings: the parent
 * owns the column they stack in.
 */
const SedeList = ({
  groupName,
  sedes,
  summary,
  activeSedeId,
  onSelect,
  onOpenInNewTab,
  afterHeader,
  onOverview,
  searchFocus,
  showSearchShortcut,
}) => {
  const [query, setQuery] = useState('');
  const searchInput = useRef(null);

  useEffect(() => {
    if (searchFocus) searchInput.current.focus();
  }, [searchFocus]);

  const selectable = getSelectableSedes(sedes);
  const withoutWhatsapp = sedes.length - selectable.length;
  const visible = selectable.filter((sede) => matches(sede, query));

  return (
    <>
      <SedeHeader>
        <SedeHeaderText>
          <GroupName>{groupName}</GroupName>
          <SedeSubtitle>{sedeCount(selectable.length, 'con WhatsApp conectado')}</SedeSubtitle>
        </SedeHeaderText>
        <PartnerPill>Acceso de socio</PartnerPill>
      </SedeHeader>

      {afterHeader}

      <SedeSearch>
        <SearchIcon />
        <input
          ref={searchInput}
          type="search"
          value={query}
          placeholder="Buscar sede o número"
          aria-label="Buscar sede o número"
          onChange={(event) => setQuery(event.target.value)}
        />
        {showSearchShortcut && (
          <SearchShortcut aria-hidden="true">{isApplePlatform() ? '⌘K' : 'Ctrl K'}</SearchShortcut>
        )}
      </SedeSearch>

      {onOverview && <SedeOverviewCard sedes={selectable} summary={summary} onOpen={onOverview} />}

      <SedeSectionLabel>Sedes</SedeSectionLabel>
      <SedeRows>
        {visible.map((sede) => {
          const isActive = sede.companyId === activeSedeId;
          const waiting = summary?.[sede.companyId]?.waiting || 0;
          const late = summary?.[sede.companyId]?.late || 0;
          return (
            <SedeItem key={sede.companyId} active={isActive}>
              <SedeRowButton
                type="button"
                active={isActive}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => {
                  if (!isActive) onSelect(sede.companyId);
                }}
              >
                <SedeMonogram style={monogramStyle(sede.color)} aria-hidden="true">{sede.monogram}</SedeMonogram>
                <SedeContent>
                  <SedeTitleLine>
                    <SedeLabel>{sede.label}</SedeLabel>
                    {isActive && <ActiveMark>Activa</ActiveMark>}
                  </SedeTitleLine>
                  <SedePhoneLine>
                    <SedePhones>
                      <WhatsappIcon />
                      <SedePhone>{sede.phones.first}</SedePhone>
                      {sede.phones.more > 0 && (
                        <SedeMorePhones>{`+${sede.phones.more}`}</SedeMorePhones>
                      )}
                    </SedePhones>
                  </SedePhoneLine>
                </SedeContent>
                {waiting > 0 && (
                  <WaitingSlot>
                    <WaitingPill
                      data-emphasis={late > 0 ? 'late' : 'neutral'}
                      title={`${waiting} esperando`}
                    >
                      {`${waiting} esperando`}
                    </WaitingPill>
                  </WaitingSlot>
                )}
              </SedeRowButton>
              {onOpenInNewTab && (
                <NewTabButton
                  type="button"
                  aria-label="Abrir en una pestaña nueva"
                  title="Abrir en una pestaña nueva"
                  onClick={() => onOpenInNewTab(sede.companyId)}
                >
                  <NewTabIcon />
                </NewTabButton>
              )}
            </SedeItem>
          );
        })}
      </SedeRows>

      {visible.length === 0 && selectable.length > 0 && (
        <SedeEmptyLine>Ninguna sede coincide. Busca por nombre de sede o por número.</SedeEmptyLine>
      )}
      {withoutWhatsapp > 0 && (
        <SedeMutedLine>{sedeCount(withoutWhatsapp, 'sin WhatsApp conectado')}</SedeMutedLine>
      )}
    </>
  );
};

SedeList.propTypes = {
  groupName: PropTypes.string,
  sedes: PropTypes.arrayOf(PropTypes.shape({
    companyId: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    monogram: PropTypes.string,
    color: PropTypes.string,
    channels: PropTypes.arrayOf(PropTypes.object),
  })).isRequired,
  // Who is waiting in each sede, by company id; a courtesy that may be missing.
  summary: PropTypes.objectOf(PropTypes.shape({
    waiting: PropTypes.number,
    late: PropTypes.number,
  })),
  // The sede the tab already works: marked, and not picked again.
  activeSedeId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  // Without it the rows have no "new tab" action.
  onOpenInNewTab: PropTypes.func,
  // Rendered between the header and the search.
  afterHeader: PropTypes.node,
  // With it the list offers the overview of all the sedes, over the rows.
  onOverview: PropTypes.func,
  // The search takes the focus each time this changes to a truthy value.
  searchFocus: PropTypes.number,
  // Shows the shortcut of the parent that opens it on the search.
  showSearchShortcut: PropTypes.bool,
};

SedeList.defaultProps = {
  groupName: '',
  summary: null,
  activeSedeId: null,
  onOpenInNewTab: undefined,
  afterHeader: null,
  onOverview: undefined,
  searchFocus: 0,
  showSearchShortcut: false,
};

export default SedeList;
